import { createContext, useContext, useEffect, useMemo, useState } from "react";
import {
  BrowserRouter,
  Link,
  Navigate,
  NavLink,
  Outlet,
  Route,
  Routes,
  useLocation,
  useNavigate,
} from "react-router";
import {
  Archive,
  CheckCircle2,
  ChevronRight,
  FileQuestion,
  Globe2,
  LayoutDashboard,
  LibraryBig,
  ListChecks,
  LogOut,
  Plus,
  Settings,
  ShieldCheck,
  Sparkles,
} from "lucide-react";
import { toast, Toaster } from "sonner";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Sheet, SheetContent, SheetDescription, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { Textarea } from "@/components/ui/textarea";
import { cn } from "@/lib/utils";
import { ApiUser, apiFetch, patchJson, postJson } from "@/lib/cloudApi";
import StudentPortal from "./StudentPortal";

type AuthState = {
  user: ApiUser | null;
  setupRequired: boolean;
  loading: boolean;
  refresh: () => Promise<void>;
  setUser: (user: ApiUser | null) => void;
};

type Language = {
  id: string;
  code: string;
  name: string;
  nativeName: string;
  direction: string;
};

type Family = {
  id: string;
  code: string;
  name: string;
  description?: string;
  defaultLanguageCode?: string;
};

type Level = {
  id: string;
  code: string;
  label: string;
  scale: string;
  sortOrder: number;
};

type TemplateSummary = {
  id: string;
  code: string;
  name: string;
  levelCode: string;
  status: string;
  description?: string;
  targetAudience?: string;
  totalDurationSec: number;
  totalPoints: number;
  passingScore: number;
  family: { id: string; code: string; name: string };
  language: { id: string; code: string; name: string; nativeName: string };
  skillCount: number;
  taskCount: number;
};

type TemplateDetail = TemplateSummary & {
  skills: Array<{
    id: string;
    key: string;
    name: string;
    sortOrder: number;
    durationSec: number;
    maxPoints: number;
    sections: Array<{
      id: string;
      key: string;
      name: string;
      instructions?: string;
      durationSec: number;
      maxPoints: number;
      tasks: Array<{
        id: string;
        key: string;
        taskType: string;
        name: string;
        itemCount: number;
        maxPoints: number;
      }>;
    }>;
  }>;
};

type Question = {
  id: string;
  title: string;
  stem: string;
  levelCode: string;
  skillKey: string;
  taskType: string;
  status: string;
  difficulty: number;
  choiceCount: number;
  family?: { code: string; name: string } | null;
  language: { code: string; name: string; nativeName?: string };
};

type QuestionFormState = {
  templateId: string;
  templateTaskId: string;
  title: string;
  stem: string;
  explanation: string;
  difficulty: number;
  status: string;
  choices: Array<{ text: string; isCorrect: boolean }>;
};

type TemplateTaskOption = {
  templateId: string;
  templateName: string;
  familyId: string;
  familyName: string;
  languageId: string;
  languageName: string;
  levelCode: string;
  skillKey: string;
  skillName: string;
  sectionName: string;
  taskId: string;
  taskName: string;
  taskType: string;
  itemCount: number;
  maxPoints: number;
};

type MockExam = {
  id: string;
  code: string;
  title: string;
  status: string;
  visibility: string;
  taskCount?: number;
  questionCount?: number;
  pointTotal?: number;
  template: { id: string; name: string; code: string; levelCode: string };
};

const AuthContext = createContext<AuthState | null>(null);

type UiLocale = "en" | "tr" | "de";

type UiState = {
  locale: UiLocale;
  setLocale: (locale: UiLocale) => void;
  t: (key: UiTextKey) => string;
};

const localeLabels: Record<UiLocale, string> = {
  en: "English",
  tr: "Turkce",
  de: "Deutsch",
};

const uiText = {
  en: {
    loading: "Loading...",
    dataLoading: "Loading data...",
    dataError: "Data could not be loaded",
    productTag: "Exam Management",
    heroTitle: "Multilingual exam preparation platform",
    heroText:
      "Manage exam families, templates, question banks and practice exams for German, English, Turkish and more languages in one professional workspace.",
    featureContent: "Structured content",
    featureTemplates: "Template builder",
    featureBusiness: "Subscription ready",
    setupTitle: "First admin setup",
    setupDesc: "This screen is available only while no admin user exists.",
    loginTitle: "Sign in",
    loginDesc: "Sign in to continue to your workspace.",
    name: "Full name",
    email: "Email",
    password: "Password",
    createAdmin: "Create admin",
    adminCreated: "Admin account created",
    setupFailed: "Setup failed",
    loginButton: "Sign in",
    loginFailed: "Sign in failed",
    navDashboard: "Overview",
    navTemplates: "Exam Templates",
    navQuestions: "Questions",
    navMocks: "Practice Exams",
    navSettings: "Settings",
    brandSubtitle: "Admin workspace",
    headerTagline: "Content management for exam preparation",
    workspaceBadge: "Admin",
    viewStudent: "Student view",
    logout: "Sign out",
    dashboardTitle: "Overview",
    dashboardDesc: "Content and operations summary for the exam platform.",
    statsTemplates: "Templates",
    statsQuestions: "Questions",
    statsMocks: "Practice Exams",
    statsSubscribers: "Subscribers",
    recentTemplates: "Recent Templates",
    recentTemplatesDesc: "Latest multilingual exam structures",
    refresh: "Refresh",
    opsTitle: "Ready to publish",
    opsDesc: "The platform now separates content, exams and subscriptions cleanly.",
    opsLanguages: "Languages and exam families are managed separately",
    opsExamFlow: "Templates and practice exams are separate",
    opsFiles: "Files belong inside the related question or exam part",
    templatesTitle: "Exam Templates",
    templatesDesc: "Define language, exam type, level and section structure independently.",
    newTemplate: "New template",
    templateCardDesc: "Start a reusable structure for TELC, DTZ, IELTS or any other exam.",
    templateName: "Template name",
    examFamily: "Exam type",
    language: "Language",
    level: "Level",
    points: "Points",
    passingScore: "Pass score",
    description: "Description",
    createTemplate: "Create template",
    templateCreated: "Template created",
    templateCreateFailed: "Template could not be created",
    templatePublished: "Template published",
    templateUpdated: "Template updated",
    templateList: "Template list",
    templateListDesc: "Exam structures ready for publishing",
    review: "Review",
    publish: "Publish",
    open: "Open",
    noTemplatesTitle: "No templates yet",
    noTemplatesDesc: "Create the first exam template to begin.",
    tableTemplate: "Template",
    tableLanguage: "Language",
    tableStructure: "Structure",
    tableStatus: "Status",
    sectionLabel: "sections",
    partLabel: "parts",
    questionsTitle: "Questions",
    questionsDesc: "Keep questions reusable so they can be used in different practice exams.",
    newQuestion: "New question",
    newQuestionDesc: "Add questions through a guided template, section and part workflow.",
    openQuestionBuilder: "Add question",
    questionBankIntro: "Questions stay reusable, but each one is now linked to the correct template part.",
    questionContext: "Question context",
    chooseTemplate: "Choose template",
    choosePart: "Choose exam part",
    selectedStructure: "Selected structure",
    answerOptions: "Answer options",
    addOption: "Add option",
    remove: "Remove",
    explanation: "Explanation",
    difficulty: "Difficulty",
    manualQuestionHint: "This part is evaluated manually, so answer options are not required.",
    noTemplateTasks: "This template has no parts yet. Add the structure in Templates first.",
    templateNeeded: "Create or select a template before adding questions.",
    title: "Title",
    questionStem: "Question text",
    family: "Exam type",
    skill: "Skill",
    type: "Type",
    option: "Option",
    correct: "Correct",
    addQuestion: "Add question",
    questionCreated: "Question added",
    questionCreateFailed: "Question could not be added",
    questionList: "Question list",
    questionListDesc: "Approved questions can be used in published exams.",
    noQuestionsTitle: "No questions yet",
    noQuestionsDesc: "Add the first question to the bank.",
    catalog: "Catalog",
    mockTitle: "Practice Exams",
    mockDesc: "Create practice exams from templates for subscribers.",
    newMock: "New practice exam",
    newMockDesc: "Choose a template, visibility and publishing status.",
    mockName: "Practice exam name",
    templateLabel: "Template",
    visibility: "Visibility",
    status: "Status",
    createMock: "Create practice exam",
    mockCreated: "Practice exam created",
    mockCreateFailed: "Practice exam could not be created",
    mocks: "Practice exams",
    mocksDesc: "These records will later power the student exam flow.",
    noMocksTitle: "No practice exams yet",
    noMocksDesc: "Create the first practice exam from a template.",
    taskCount: "parts",
    questionCount: "questions",
    pointCount: "points",
    settingsTitle: "Settings",
    settingsDesc: "Languages, exam types and basic catalog settings.",
    verifyCatalog: "Check catalog",
    catalogChecked: "Catalog data checked",
    languagesTitle: "Languages",
    languagesDesc: "German, English, Turkish or any other target language.",
    languageAdded: "Language added",
    examFamiliesTitle: "Exam types",
    examFamiliesDesc: "TELC, DTZ, Goethe, IELTS or custom exam types.",
    familyAdded: "Exam type added",
    addExamFamily: "Add exam type",
    emptyUrl: "No file attached",
    minutes: "min",
  },
  tr: {
    loading: "Yukleniyor...",
    dataLoading: "Veriler yukleniyor...",
    dataError: "Veri alinamadi",
    productTag: "Sinav Yonetimi",
    heroTitle: "Cok dilli sinav hazirlik platformu",
    heroText:
      "Almanca, Ingilizce, Turkce ve diger diller icin sinav tiplerini, sablonlari, soru bankasini ve deneme sinavlarini tek profesyonel alanda yonetin.",
    featureContent: "Duzenli icerik",
    featureTemplates: "Sablon olusturma",
    featureBusiness: "Abonelige hazir",
    setupTitle: "Ilk admin kurulumu",
    setupDesc: "Bu ekran yalnizca sistemde admin kullanici yokken acilir.",
    loginTitle: "Giris",
    loginDesc: "Calisma alanina devam etmek icin giris yap.",
    name: "Ad Soyad",
    email: "E-posta",
    password: "Sifre",
    createAdmin: "Admin olustur",
    adminCreated: "Admin hesabi olusturuldu",
    setupFailed: "Kurulum basarisiz",
    loginButton: "Giris yap",
    loginFailed: "Giris basarisiz",
    navDashboard: "Genel Bakis",
    navTemplates: "Sinav Sablonlari",
    navQuestions: "Sorular",
    navMocks: "Deneme Sinavlari",
    navSettings: "Ayarlar",
    brandSubtitle: "Yonetim alani",
    headerTagline: "Sinav hazirlik icerik yonetimi",
    workspaceBadge: "Yonetim",
    viewStudent: "Ogrenci gorunumu",
    logout: "Cikis",
    dashboardTitle: "Genel Bakis",
    dashboardDesc: "Sinav platformunun icerik ve operasyon ozeti.",
    statsTemplates: "Sablonlar",
    statsQuestions: "Sorular",
    statsMocks: "Denemeler",
    statsSubscribers: "Aboneler",
    recentTemplates: "Son Sablonlar",
    recentTemplatesDesc: "Son cok dilli sinav yapilari",
    refresh: "Yenile",
    opsTitle: "Yayina hazir",
    opsDesc: "Platform icerik, sinav ve abonelikleri temiz sekilde ayirir.",
    opsLanguages: "Diller ve sinav tipleri ayri yonetilir",
    opsExamFlow: "Sablonlar ve deneme sinavlari ayridir",
    opsFiles: "Dosyalar ilgili soru veya sinav bolumu icinde durur",
    templatesTitle: "Sinav Sablonlari",
    templatesDesc: "Dil, sinav tipi, seviye ve bolum yapisini bagimsiz tanimla.",
    newTemplate: "Yeni sablon",
    templateCardDesc: "TELC, DTZ, IELTS veya herhangi bir sinav icin tekrar kullanilabilir yapi olustur.",
    templateName: "Sablon adi",
    examFamily: "Sinav tipi",
    language: "Dil",
    level: "Seviye",
    points: "Puan",
    passingScore: "Gecme puani",
    description: "Aciklama",
    createTemplate: "Sablon olustur",
    templateCreated: "Sablon olusturuldu",
    templateCreateFailed: "Sablon olusturulamadi",
    templatePublished: "Sablon yayina alindi",
    templateUpdated: "Sablon guncellendi",
    templateList: "Sablon listesi",
    templateListDesc: "Yayina alinacak sinav yapilari",
    review: "Kontrol",
    publish: "Yayinla",
    open: "Ac",
    noTemplatesTitle: "Henuz sablon yok",
    noTemplatesDesc: "Baslamak icin ilk sinav sablonunu olustur.",
    tableTemplate: "Sablon",
    tableLanguage: "Dil",
    tableStructure: "Yapi",
    tableStatus: "Durum",
    sectionLabel: "bolum",
    partLabel: "parca",
    questionsTitle: "Sorular",
    questionsDesc: "Sorulari farkli denemelerde tekrar kullanilabilecek sekilde tut.",
    newQuestion: "Yeni soru",
    newQuestionDesc: "Soruyu sablon, bolum ve parca secerek daha kontrollu ekle.",
    openQuestionBuilder: "Soru ekle",
    questionBankIntro: "Sorular tekrar kullanilabilir kalir, ama her soru dogru sablon bolumune baglanir.",
    questionContext: "Soru baglami",
    chooseTemplate: "Sablon sec",
    choosePart: "Sinav bolumu sec",
    selectedStructure: "Secilen yapi",
    answerOptions: "Cevap secenekleri",
    addOption: "Secenek ekle",
    remove: "Kaldir",
    explanation: "Aciklama",
    difficulty: "Zorluk",
    manualQuestionHint: "Bu bolum manuel degerlendirilir, bu yuzden cevap secenekleri zorunlu degil.",
    noTemplateTasks: "Bu sablonda henuz bolum yok. Once Sablonlar alaninda yapisini ekle.",
    templateNeeded: "Soru eklemeden once bir sablon olustur veya sec.",
    title: "Baslik",
    questionStem: "Soru metni",
    family: "Sinav tipi",
    skill: "Beceri",
    type: "Tip",
    option: "Secenek",
    correct: "Dogru",
    addQuestion: "Soru ekle",
    questionCreated: "Soru eklendi",
    questionCreateFailed: "Soru eklenemedi",
    questionList: "Soru listesi",
    questionListDesc: "Onaylanan sorular yayindaki sinavlarda kullanilabilir.",
    noQuestionsTitle: "Henuz soru yok",
    noQuestionsDesc: "Bankaya ilk soruyu ekle.",
    catalog: "Katalog",
    mockTitle: "Deneme Sinavlari",
    mockDesc: "Abonelere acilacak deneme sinavlarini sablonlardan olustur.",
    newMock: "Yeni deneme",
    newMockDesc: "Sablon, gorunurluk ve yayin durumunu sec.",
    mockName: "Deneme adi",
    templateLabel: "Sablon",
    visibility: "Gorunurluk",
    status: "Durum",
    createMock: "Deneme olustur",
    mockCreated: "Deneme sinavi olusturuldu",
    mockCreateFailed: "Deneme olusturulamadi",
    mocks: "Denemeler",
    mocksDesc: "Bu kayitlar sonraki asamada ogrenci sinav akisini besler.",
    noMocksTitle: "Henuz deneme yok",
    noMocksDesc: "Ilk deneme sinavini bir sablondan olustur.",
    taskCount: "parca",
    questionCount: "soru",
    pointCount: "puan",
    settingsTitle: "Ayarlar",
    settingsDesc: "Diller, sinav tipleri ve temel katalog ayarlari.",
    verifyCatalog: "Katalog kontrolu",
    catalogChecked: "Katalog verileri kontrol edildi",
    languagesTitle: "Diller",
    languagesDesc: "Almanca, Ingilizce, Turkce veya yeni hedef diller.",
    languageAdded: "Dil eklendi",
    examFamiliesTitle: "Sinav tipleri",
    examFamiliesDesc: "TELC, DTZ, Goethe, IELTS veya ozel sinav tipleri.",
    familyAdded: "Sinav tipi eklendi",
    addExamFamily: "Sinav tipi ekle",
    emptyUrl: "Dosya eklenmedi",
    minutes: "dk",
  },
  de: {
    loading: "Wird geladen...",
    dataLoading: "Daten werden geladen...",
    dataError: "Daten konnten nicht geladen werden",
    productTag: "Pruefungsverwaltung",
    heroTitle: "Mehrsprachige Plattform zur Pruefungsvorbereitung",
    heroText:
      "Verwalten Sie Pruefungstypen, Vorlagen, Fragen und Probepruefungen fuer Deutsch, Englisch, Tuerkisch und weitere Sprachen an einem Ort.",
    featureContent: "Strukturierte Inhalte",
    featureTemplates: "Vorlagen erstellen",
    featureBusiness: "Bereit fuer Abos",
    setupTitle: "Erste Admin-Einrichtung",
    setupDesc: "Diese Seite ist nur verfuegbar, solange noch kein Admin existiert.",
    loginTitle: "Anmeldung",
    loginDesc: "Melden Sie sich an, um fortzufahren.",
    name: "Name",
    email: "E-Mail",
    password: "Passwort",
    createAdmin: "Admin erstellen",
    adminCreated: "Admin-Konto erstellt",
    setupFailed: "Einrichtung fehlgeschlagen",
    loginButton: "Anmelden",
    loginFailed: "Anmeldung fehlgeschlagen",
    navDashboard: "Uebersicht",
    navTemplates: "Pruefungsvorlagen",
    navQuestions: "Fragen",
    navMocks: "Probepruefungen",
    navSettings: "Einstellungen",
    brandSubtitle: "Admin-Bereich",
    headerTagline: "Inhaltsverwaltung fuer Pruefungsvorbereitung",
    workspaceBadge: "Admin",
    viewStudent: "Lernansicht",
    logout: "Abmelden",
    dashboardTitle: "Uebersicht",
    dashboardDesc: "Zusammenfassung von Inhalten und Betrieb der Pruefungsplattform.",
    statsTemplates: "Vorlagen",
    statsQuestions: "Fragen",
    statsMocks: "Probepruefungen",
    statsSubscribers: "Abos",
    recentTemplates: "Neueste Vorlagen",
    recentTemplatesDesc: "Aktuelle mehrsprachige Pruefungsstrukturen",
    refresh: "Aktualisieren",
    opsTitle: "Bereit zur Veroeffentlichung",
    opsDesc: "Inhalte, Pruefungen und Abos sind sauber getrennt.",
    opsLanguages: "Sprachen und Pruefungstypen werden getrennt verwaltet",
    opsExamFlow: "Vorlagen und Probepruefungen sind getrennt",
    opsFiles: "Dateien gehoeren in die passende Frage oder den Pruefungsteil",
    templatesTitle: "Pruefungsvorlagen",
    templatesDesc: "Sprache, Pruefungstyp, Niveau und Abschnittsstruktur unabhaengig definieren.",
    newTemplate: "Neue Vorlage",
    templateCardDesc: "Erstellen Sie eine wiederverwendbare Struktur fuer TELC, DTZ, IELTS oder andere Pruefungen.",
    templateName: "Vorlagenname",
    examFamily: "Pruefungstyp",
    language: "Sprache",
    level: "Niveau",
    points: "Punkte",
    passingScore: "Bestehensgrenze",
    description: "Beschreibung",
    createTemplate: "Vorlage erstellen",
    templateCreated: "Vorlage erstellt",
    templateCreateFailed: "Vorlage konnte nicht erstellt werden",
    templatePublished: "Vorlage veroeffentlicht",
    templateUpdated: "Vorlage aktualisiert",
    templateList: "Vorlagenliste",
    templateListDesc: "Pruefungsstrukturen fuer die Veroeffentlichung",
    review: "Pruefen",
    publish: "Veroeffentlichen",
    open: "Oeffnen",
    noTemplatesTitle: "Noch keine Vorlagen",
    noTemplatesDesc: "Erstellen Sie die erste Pruefungsvorlage.",
    tableTemplate: "Vorlage",
    tableLanguage: "Sprache",
    tableStructure: "Struktur",
    tableStatus: "Status",
    sectionLabel: "Abschnitte",
    partLabel: "Teile",
    questionsTitle: "Fragen",
    questionsDesc: "Fragen wiederverwendbar halten, damit sie in mehreren Probepruefungen genutzt werden koennen.",
    newQuestion: "Neue Frage",
    newQuestionDesc: "Fragen ueber Vorlage, Abschnitt und Pruefungsteil sauber anlegen.",
    openQuestionBuilder: "Frage hinzufuegen",
    questionBankIntro: "Fragen bleiben wiederverwendbar und sind zugleich mit dem passenden Vorlagenteil verknuepft.",
    questionContext: "Fragenkontext",
    chooseTemplate: "Vorlage waehlen",
    choosePart: "Pruefungsteil waehlen",
    selectedStructure: "Gewaehlte Struktur",
    answerOptions: "Antwortoptionen",
    addOption: "Option hinzufuegen",
    remove: "Entfernen",
    explanation: "Erklaerung",
    difficulty: "Schwierigkeit",
    manualQuestionHint: "Dieser Teil wird manuell bewertet, daher sind Antwortoptionen nicht erforderlich.",
    noTemplateTasks: "Diese Vorlage hat noch keine Teile. Struktur zuerst im Vorlagenbereich anlegen.",
    templateNeeded: "Vor dem Hinzufuegen einer Frage bitte eine Vorlage erstellen oder auswaehlen.",
    title: "Titel",
    questionStem: "Fragetext",
    family: "Pruefungstyp",
    skill: "Kompetenz",
    type: "Typ",
    option: "Option",
    correct: "Richtig",
    addQuestion: "Frage hinzufuegen",
    questionCreated: "Frage hinzugefuegt",
    questionCreateFailed: "Frage konnte nicht hinzugefuegt werden",
    questionList: "Fragenliste",
    questionListDesc: "Freigegebene Fragen koennen in veroeffentlichten Pruefungen verwendet werden.",
    noQuestionsTitle: "Noch keine Fragen",
    noQuestionsDesc: "Fuegen Sie die erste Frage hinzu.",
    catalog: "Katalog",
    mockTitle: "Probepruefungen",
    mockDesc: "Erstellen Sie Probepruefungen aus Vorlagen fuer Abonnenten.",
    newMock: "Neue Probepruefung",
    newMockDesc: "Vorlage, Sichtbarkeit und Veroeffentlichungsstatus waehlen.",
    mockName: "Name der Probepruefung",
    templateLabel: "Vorlage",
    visibility: "Sichtbarkeit",
    status: "Status",
    createMock: "Probepruefung erstellen",
    mockCreated: "Probepruefung erstellt",
    mockCreateFailed: "Probepruefung konnte nicht erstellt werden",
    mocks: "Probepruefungen",
    mocksDesc: "Diese Eintraege speisen spaeter den Ablauf fuer Lernende.",
    noMocksTitle: "Noch keine Probepruefungen",
    noMocksDesc: "Erstellen Sie die erste Probepruefung aus einer Vorlage.",
    taskCount: "Teile",
    questionCount: "Fragen",
    pointCount: "Punkte",
    settingsTitle: "Einstellungen",
    settingsDesc: "Sprachen, Pruefungstypen und Katalogeinstellungen.",
    verifyCatalog: "Katalog pruefen",
    catalogChecked: "Katalogdaten geprueft",
    languagesTitle: "Sprachen",
    languagesDesc: "Deutsch, Englisch, Tuerkisch oder weitere Zielsprachen.",
    languageAdded: "Sprache hinzugefuegt",
    examFamiliesTitle: "Pruefungstypen",
    examFamiliesDesc: "TELC, DTZ, Goethe, IELTS oder eigene Pruefungstypen.",
    familyAdded: "Pruefungstyp hinzugefuegt",
    addExamFamily: "Pruefungstyp hinzufuegen",
    emptyUrl: "Keine Datei angehaengt",
    minutes: "Min.",
  },
} satisfies Record<UiLocale, Record<string, string>>;

type UiTextKey = keyof typeof uiText.en;

const UiContext = createContext<UiState | null>(null);

function useAuth() {
  const value = useContext(AuthContext);
  if (!value) throw new Error("Auth context is missing");
  return value;
}

function useUi() {
  const value = useContext(UiContext);
  if (!value) throw new Error("UI context is missing");
  return value;
}

function isAdminRole(role?: string) {
  return ["owner", "admin", "editor", "support"].includes(String(role));
}

function UiProvider({ children }: { children: React.ReactNode }) {
  const [locale, setLocaleState] = useState<UiLocale>(() => {
    if (typeof window === "undefined") return "en";
    const saved = window.localStorage.getItem("admin_locale");
    return saved === "tr" || saved === "de" ? saved : "en";
  });

  const setLocale = (nextLocale: UiLocale) => {
    setLocaleState(nextLocale);
    window.localStorage.setItem("admin_locale", nextLocale);
  };

  const value = useMemo(
    () => ({
      locale,
      setLocale,
      t: (key: UiTextKey) => uiText[locale][key] || uiText.en[key],
    }),
    [locale]
  );

  return <UiContext.Provider value={value}>{children}</UiContext.Provider>;
}

function AuthProvider({ children }: { children: React.ReactNode }) {
  const [user, setUser] = useState<ApiUser | null>(null);
  const [setupRequired, setSetupRequired] = useState(false);
  const [loading, setLoading] = useState(true);

  const refresh = async () => {
    setLoading(true);
    try {
      const data = await apiFetch<{ user: ApiUser | null; setupRequired: boolean }>("/api/auth/me");
      setUser(data.user);
      setSetupRequired(data.setupRequired);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    refresh().catch(() => {
      setUser(null);
      setLoading(false);
    });
  }, []);

  const value = useMemo(
    () => ({ user, setupRequired, loading, refresh, setUser }),
    [user, setupRequired, loading]
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

function RequireAuth({ children }: { children: React.ReactNode }) {
  const auth = useAuth();
  const location = useLocation();

  if (auth.loading) return <LoadingScreen />;
  if (auth.setupRequired) return <Navigate to="/setup" replace />;
  if (!auth.user) return <Navigate to="/login" replace state={{ from: location.pathname }} />;

  return <>{children}</>;
}

function LoadingScreen() {
  const { t } = useUi();
  return (
    <div className="flex min-h-screen items-center justify-center bg-background">
      <div className="rounded-lg border bg-white px-5 py-4 text-sm shadow-sm">{t("loading")}</div>
    </div>
  );
}

function AuthCard({ title, description, children }: { title: string; description: string; children: React.ReactNode }) {
  const { t } = useUi();
  return (
    <div className="min-h-screen bg-[linear-gradient(135deg,#f8f4ee_0%,#ffffff_42%,#f5eadf_100%)]">
      <div className="mx-auto grid min-h-screen max-w-6xl grid-cols-1 lg:grid-cols-[1fr_420px]">
        <section className="flex flex-col justify-between px-6 py-8 lg:px-12">
          <div className="flex items-center justify-between gap-4">
            <Link to="/" className="flex items-center gap-3 font-semibold">
              <span className="grid h-9 w-9 place-items-center rounded-lg bg-[#d94205] text-white">BO</span>
              <span>Exam Studio</span>
            </Link>
            <LanguageSwitcher />
          </div>
          <div className="max-w-2xl py-16">
            <Badge className="mb-5 bg-[#252525] text-white hover:bg-[#252525]">{t("productTag")}</Badge>
            <h1 className="text-4xl font-bold leading-tight text-[#151515] lg:text-6xl">
              {t("heroTitle")}
            </h1>
            <p className="mt-5 max-w-xl text-base leading-7 text-[#555]">
              {t("heroText")}
            </p>
          </div>
          <div className="grid max-w-2xl grid-cols-1 gap-3 text-sm text-[#444] sm:grid-cols-3">
            <span className="rounded-lg border bg-white/70 p-3">{t("featureContent")}</span>
            <span className="rounded-lg border bg-white/70 p-3">{t("featureTemplates")}</span>
            <span className="rounded-lg border bg-white/70 p-3">{t("featureBusiness")}</span>
          </div>
        </section>
        <main className="flex items-center px-6 py-10">
          <Card className="w-full border-[#ded6cc] bg-white/95 shadow-xl">
            <CardHeader>
              <CardTitle className="text-2xl">{title}</CardTitle>
              <CardDescription>{description}</CardDescription>
            </CardHeader>
            <CardContent>{children}</CardContent>
          </Card>
        </main>
      </div>
    </div>
  );
}

function LanguageSwitcher() {
  const { locale, setLocale } = useUi();

  return (
    <div className="flex items-center gap-2 rounded-full border bg-white/80 px-2 py-1 shadow-sm">
      <Globe2 className="h-4 w-4 text-[#d94205]" />
      {(Object.keys(localeLabels) as UiLocale[]).map((item) => (
        <button
          key={item}
          type="button"
          onClick={() => setLocale(item)}
          className={cn(
            "rounded-full px-2.5 py-1 text-xs font-semibold transition-colors",
            locale === item ? "bg-[#252525] text-white" : "text-[#555] hover:bg-[#f4eee7]"
          )}
          aria-label={localeLabels[item]}
        >
          {item.toUpperCase()}
        </button>
      ))}
    </div>
  );
}

function SetupPage() {
  const auth = useAuth();
  const { t } = useUi();
  const navigate = useNavigate();
  const [form, setForm] = useState({ name: "", email: "", password: "" });
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!auth.loading && !auth.setupRequired && auth.user) navigate("/admin", { replace: true });
  }, [auth.loading, auth.setupRequired, auth.user, navigate]);

  const submit = async (event: React.FormEvent) => {
    event.preventDefault();
    setBusy(true);
    try {
      const data = await postJson<{ user: ApiUser }>("/api/auth/setup", form);
      auth.setUser(data.user);
      toast.success(t("adminCreated"));
      navigate("/admin", { replace: true });
    } catch (error) {
      toast.error(error instanceof Error ? error.message : t("setupFailed"));
    } finally {
      setBusy(false);
    }
  };

  return (
    <AuthCard title={t("setupTitle")} description={t("setupDesc")}>
      <form onSubmit={submit} className="space-y-4">
        <Field label={t("name")}>
          <Input value={form.name} onChange={(event) => setForm({ ...form, name: event.target.value })} />
        </Field>
        <Field label={t("email")}>
          <Input type="email" required value={form.email} onChange={(event) => setForm({ ...form, email: event.target.value })} />
        </Field>
        <Field label={t("password")}>
          <Input
            type="password"
            required
            minLength={10}
            value={form.password}
            onChange={(event) => setForm({ ...form, password: event.target.value })}
          />
        </Field>
        <Button className="w-full" disabled={busy}>
          <ShieldCheck className="h-4 w-4" />
          {t("createAdmin")}
        </Button>
      </form>
    </AuthCard>
  );
}

function LoginPage() {
  const auth = useAuth();
  const { t } = useUi();
  const navigate = useNavigate();
  const [form, setForm] = useState({ email: "", password: "" });
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!auth.loading && auth.setupRequired) navigate("/setup", { replace: true });
    if (!auth.loading && auth.user) navigate(isAdminRole(auth.user.role) ? "/admin" : "/student", { replace: true });
  }, [auth.loading, auth.setupRequired, auth.user, navigate]);

  const submit = async (event: React.FormEvent) => {
    event.preventDefault();
    setBusy(true);
    try {
      const data = await postJson<{ user: ApiUser }>("/api/auth/login", form);
      auth.setUser(data.user);
      navigate(isAdminRole(data.user.role) ? "/admin" : "/student", { replace: true });
    } catch (error) {
      toast.error(error instanceof Error ? error.message : t("loginFailed"));
    } finally {
      setBusy(false);
    }
  };

  return (
    <AuthCard title={t("loginTitle")} description={t("loginDesc")}>
      <form onSubmit={submit} className="space-y-4">
        <Field label={t("email")}>
          <Input type="email" required value={form.email} onChange={(event) => setForm({ ...form, email: event.target.value })} />
        </Field>
        <Field label={t("password")}>
          <Input
            type="password"
            required
            value={form.password}
            onChange={(event) => setForm({ ...form, password: event.target.value })}
          />
        </Field>
        <Button className="w-full" disabled={busy}>
          {t("loginButton")}
        </Button>
      </form>
    </AuthCard>
  );
}

function AdminShell() {
  const auth = useAuth();
  const { t } = useUi();
  const navigate = useNavigate();
  const nav = [
    { to: "/admin", label: t("navDashboard"), icon: LayoutDashboard, end: true },
    { to: "/admin/templates", label: t("navTemplates"), icon: LibraryBig },
    { to: "/admin/questions", label: t("navQuestions"), icon: FileQuestion },
    { to: "/admin/mock-exams", label: t("navMocks"), icon: ListChecks },
    { to: "/admin/settings", label: t("navSettings"), icon: Settings },
  ];

  const logout = async () => {
    await postJson("/api/auth/logout", {});
    auth.setUser(null);
    navigate("/login", { replace: true });
  };

  if (!isAdminRole(auth.user?.role)) return <Navigate to="/student" replace />;

  return (
    <div className="flex min-h-screen bg-background text-[#171717]">
      <aside className="hidden w-72 flex-col border-r border-[#ddd2c8] bg-white lg:flex">
        <div className="flex h-16 items-center gap-3 border-b border-[#eee4da] px-5">
          <span className="grid h-9 w-9 place-items-center rounded-lg bg-[#d94205] text-sm font-bold text-white">BO</span>
          <div>
            <div className="font-semibold">Exam Studio</div>
            <div className="text-xs text-muted-foreground">{t("brandSubtitle")}</div>
          </div>
        </div>
        <nav className="flex-1 space-y-1 p-3">
          {nav.map((item) => {
            const Icon = item.icon;
            return (
              <NavLink
                key={item.to}
                to={item.to}
                end={item.end}
                className={({ isActive }) =>
                  cn(
                    "flex items-center gap-3 rounded-lg px-3 py-2.5 text-sm font-medium transition-colors",
                    isActive ? "bg-[#252525] text-white" : "text-[#3d3d3d] hover:bg-[#f4eee7]"
                  )
                }
              >
                <Icon className="h-4 w-4" />
                {item.label}
              </NavLink>
            );
          })}
        </nav>
        <div className="border-t border-[#eee4da] p-4 text-sm">
          <div className="font-medium">{auth.user?.name || auth.user?.email}</div>
          <div className="mb-3 text-xs text-muted-foreground">{auth.user?.role}</div>
          <Button variant="outline" size="sm" className="w-full justify-start" onClick={logout}>
            <LogOut className="h-4 w-4" />
            {t("logout")}
          </Button>
        </div>
      </aside>
      <main className="min-w-0 flex-1">
        <header className="sticky top-0 z-10 flex h-16 items-center justify-between border-b border-[#ddd2c8] bg-white/90 px-4 backdrop-blur lg:px-8">
          <div className="flex items-center gap-3">
            <Badge variant="outline" className="border-[#d94205] text-[#d94205]">{t("workspaceBadge")}</Badge>
            <span className="hidden text-sm text-muted-foreground sm:inline">{t("headerTagline")}</span>
          </div>
          <div className="flex items-center gap-2">
            <Button asChild variant="outline" size="sm" className="hidden sm:inline-flex">
              <Link to="/student">{t("viewStudent")}</Link>
            </Button>
            <LanguageSwitcher />
            <Button variant="ghost" size="sm" onClick={logout} className="lg:hidden">
              <LogOut className="h-4 w-4" />
            </Button>
          </div>
        </header>
        <div className="mx-auto max-w-7xl px-4 py-6 lg:px-8">
          <Outlet />
        </div>
      </main>
    </div>
  );
}

function DashboardPage() {
  const { t } = useUi();
  const { data, loading, reload } = useData<{ stats: Record<string, number>; recentTemplates: TemplateSummary[] }>("/api/admin/overview");

  if (loading) return <PageLoading />;

  const stats = data?.stats || {};
  const cards = [
    { label: t("statsTemplates"), value: stats.templates || 0, icon: LibraryBig },
    { label: t("statsQuestions"), value: stats.questions || 0, icon: FileQuestion },
    { label: t("statsMocks"), value: stats.mockExams || 0, icon: ListChecks },
    { label: t("statsSubscribers"), value: stats.activeSubscriptions || 0, icon: Sparkles },
  ];

  return (
    <Page title={t("dashboardTitle")} description={t("dashboardDesc")}>
      <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-4">
        {cards.map((card) => {
          const Icon = card.icon;
          return (
            <Card key={card.label}>
              <CardHeader className="flex-row items-center justify-between space-y-0 pb-2">
                <CardDescription>{card.label}</CardDescription>
                <Icon className="h-4 w-4 text-[#d94205]" />
              </CardHeader>
              <CardContent>
                <div className="text-3xl font-bold">{card.value}</div>
              </CardContent>
            </Card>
          );
        })}
      </div>
      <div className="mt-6 grid gap-6 lg:grid-cols-[1.4fr_.8fr]">
        <Card>
          <CardHeader className="flex-row items-center justify-between">
            <div>
              <CardTitle>{t("recentTemplates")}</CardTitle>
              <CardDescription>{t("recentTemplatesDesc")}</CardDescription>
            </div>
            <Button variant="outline" size="sm" onClick={reload}>{t("refresh")}</Button>
          </CardHeader>
          <CardContent>
            <TemplateTable templates={data?.recentTemplates || []} compact />
          </CardContent>
        </Card>
        <Card className="bg-[#252525] text-white">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <CheckCircle2 className="h-5 w-5 text-[#ffbe2e]" />
              {t("opsTitle")}
            </CardTitle>
            <CardDescription className="text-white/70">
              {t("opsDesc")}
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-3 text-sm text-white/80">
            <div className="flex items-center gap-2">
              <CheckCircle2 className="h-4 w-4 text-[#ffbe2e]" />
              {t("opsLanguages")}
            </div>
            <div className="flex items-center gap-2">
              <CheckCircle2 className="h-4 w-4 text-[#ffbe2e]" />
              {t("opsExamFlow")}
            </div>
            <div className="flex items-center gap-2">
              <CheckCircle2 className="h-4 w-4 text-[#ffbe2e]" />
              {t("opsFiles")}
            </div>
          </CardContent>
        </Card>
      </div>
    </Page>
  );
}

function TemplateStudioPage() {
  const { t } = useUi();
  const { data, loading, reload } = useData<{ templates: TemplateSummary[] }>("/api/admin/templates");
  const catalog = useData<{ languages: Language[]; families: Family[]; levels: Level[] }>("/api/admin/exam-families");
  const languages = useData<{ languages: Language[] }>("/api/admin/languages");
  const [selected, setSelected] = useState<TemplateDetail | null>(null);
  const [creating, setCreating] = useState(false);
  const [form, setForm] = useState({
    name: "",
    familyId: "",
    languageId: "",
    levelCode: "B1",
    totalDurationSec: 7200,
    totalPoints: 100,
    passingScore: 60,
    description: "",
  });

  const families = catalog.data?.families || [];
  const levels = catalog.data?.levels || [];
  const languageList = languages.data?.languages || [];

  useEffect(() => {
    setForm((current) => ({
      ...current,
      familyId: current.familyId || families[0]?.id || "",
      languageId: current.languageId || languageList[0]?.id || "",
    }));
  }, [families, languageList]);

  const createTemplate = async (event: React.FormEvent) => {
    event.preventDefault();
    setCreating(true);
    try {
      const result = await postJson<{ id: string }>("/api/admin/templates", form);
      toast.success(t("templateCreated"));
      await reload();
      await loadDetail(result.id);
      setForm((current) => ({ ...current, name: "", description: "" }));
    } catch (error) {
      toast.error(error instanceof Error ? error.message : t("templateCreateFailed"));
    } finally {
      setCreating(false);
    }
  };

  const loadDetail = async (id: string) => {
    const detail = await apiFetch<{ template: TemplateDetail }>(`/api/admin/templates/${id}`);
    setSelected(detail.template);
  };

  const publish = async (id: string, status: string) => {
    const result = await patchJson<{ template: TemplateDetail }>(`/api/admin/templates/${id}`, { status });
    setSelected(result.template);
    await reload();
    toast.success(status === "published" ? t("templatePublished") : t("templateUpdated"));
  };

  return (
    <Page title={t("templatesTitle")} description={t("templatesDesc")}>
      <div className="grid gap-6 xl:grid-cols-[420px_1fr]">
        <Card>
          <CardHeader>
            <CardTitle>{t("newTemplate")}</CardTitle>
            <CardDescription>{t("templateCardDesc")}</CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={createTemplate} className="space-y-4">
              <Field label={t("templateName")}>
                <Input required value={form.name} onChange={(event) => setForm({ ...form, name: event.target.value })} />
              </Field>
              <div className="grid grid-cols-2 gap-3">
                <Field label={t("examFamily")}>
                  <NativeSelect value={form.familyId} onChange={(value) => setForm({ ...form, familyId: value })}>
                    {families.map((family) => (
                      <option key={family.id} value={family.id}>{family.name}</option>
                    ))}
                  </NativeSelect>
                </Field>
                <Field label={t("language")}>
                  <NativeSelect value={form.languageId} onChange={(value) => setForm({ ...form, languageId: value })}>
                    {languageList.map((language) => (
                      <option key={language.id} value={language.id}>{language.nativeName}</option>
                    ))}
                  </NativeSelect>
                </Field>
              </div>
              <div className="grid grid-cols-3 gap-3">
                <Field label={t("level")}>
                  <NativeSelect value={form.levelCode} onChange={(value) => setForm({ ...form, levelCode: value })}>
                    {levels.map((level) => (
                      <option key={level.id} value={level.code}>{level.code}</option>
                    ))}
                  </NativeSelect>
                </Field>
                <Field label={t("points")}>
                  <Input type="number" value={form.totalPoints} onChange={(event) => setForm({ ...form, totalPoints: Number(event.target.value) })} />
                </Field>
                <Field label={t("passingScore")}>
                  <Input type="number" value={form.passingScore} onChange={(event) => setForm({ ...form, passingScore: Number(event.target.value) })} />
                </Field>
              </div>
              <Field label={t("description")}>
                <Textarea value={form.description} onChange={(event) => setForm({ ...form, description: event.target.value })} />
              </Field>
              <Button disabled={creating} className="w-full">
                <Plus className="h-4 w-4" />
                {t("createTemplate")}
              </Button>
            </form>
          </CardContent>
        </Card>

        <div className="space-y-6">
          <Card>
            <CardHeader className="flex-row items-center justify-between">
              <div>
                <CardTitle>{t("templateList")}</CardTitle>
                <CardDescription>{t("templateListDesc")}</CardDescription>
              </div>
              <Button variant="outline" size="sm" onClick={reload}>{t("refresh")}</Button>
            </CardHeader>
            <CardContent>
              {loading ? <PageLoading /> : <TemplateTable templates={data?.templates || []} onSelect={loadDetail} />}
            </CardContent>
          </Card>
          {selected && <TemplateDetailCard template={selected} onStatus={publish} />}
        </div>
      </div>
    </Page>
  );
}

function QuestionBankPage() {
  const { t, locale } = useUi();
  const { data, loading, reload } = useData<{ questions: Question[] }>("/api/admin/questions");
  const templates = useData<{ templates: TemplateSummary[] }>("/api/admin/templates");
  const [builderOpen, setBuilderOpen] = useState(false);
  const [selectedTemplate, setSelectedTemplate] = useState<TemplateDetail | null>(null);
  const [templateLoading, setTemplateLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState<QuestionFormState>(createEmptyQuestionForm());

  useEffect(() => {
    const firstTemplateId = templates.data?.templates?.[0]?.id || "";
    setForm((current) => ({ ...current, templateId: current.templateId || firstTemplateId }));
  }, [templates.data]);

  useEffect(() => {
    if (!form.templateId) {
      setSelectedTemplate(null);
      return;
    }

    let cancelled = false;
    setTemplateLoading(true);
    apiFetch<{ template: TemplateDetail }>(`/api/admin/templates/${form.templateId}`)
      .then((result) => {
        if (!cancelled) setSelectedTemplate(result.template);
      })
      .catch((error) => toast.error(error instanceof Error ? error.message : t("dataError")))
      .finally(() => {
        if (!cancelled) setTemplateLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [form.templateId]);

  const taskOptions = useMemo(() => buildTemplateTaskOptions(selectedTemplate), [selectedTemplate]);

  useEffect(() => {
    setForm((current) => {
      if (!taskOptions.length) return { ...current, templateTaskId: "" };
      if (taskOptions.some((option) => option.taskId === current.templateTaskId)) return current;
      return { ...current, templateTaskId: taskOptions[0].taskId };
    });
  }, [taskOptions]);

  const selectedTask = taskOptions.find((option) => option.taskId === form.templateTaskId) || null;
  const manualTask = selectedTask ? isManualTaskType(selectedTask.taskType) : false;

  const submit = async (event: React.FormEvent) => {
    event.preventDefault();
    if (!selectedTask) {
      toast.error(t("templateNeeded"));
      return;
    }
    setSaving(true);
    try {
      await postJson("/api/admin/questions", {
        title: form.title,
        stem: form.stem,
        explanation: form.explanation,
        languageId: selectedTask.languageId,
        familyId: selectedTask.familyId,
        levelCode: selectedTask.levelCode,
        skillKey: selectedTask.skillKey,
        taskType: selectedTask.taskType,
        difficulty: form.difficulty,
        status: form.status,
        choices: manualTask ? [] : form.choices,
        metadata: {
          templateId: selectedTask.templateId,
          templateTaskId: selectedTask.taskId,
          templateName: selectedTask.templateName,
          skillName: selectedTask.skillName,
          sectionName: selectedTask.sectionName,
          taskName: selectedTask.taskName,
        },
        tags: [selectedTask.familyName, selectedTask.levelCode, selectedTask.skillKey].filter(Boolean),
      });
      toast.success(t("questionCreated"));
      setForm((current) => ({
        ...current,
        title: "",
        stem: "",
        explanation: "",
        choices: current.choices.map((choice, index) => ({ text: "", isCorrect: index === 0 })),
      }));
      reload();
      setBuilderOpen(false);
    } catch (error) {
      toast.error(error instanceof Error ? error.message : t("questionCreateFailed"));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Page title={t("questionsTitle")} description={t("questionsDesc")}>
      <div className="space-y-6">
        <Card className="overflow-hidden border-[#ded6cc] bg-[#252525] text-white">
          <CardHeader className="flex flex-col justify-between gap-4 md:flex-row md:items-center">
            <div>
              <Badge className="mb-3 bg-[#d94205] text-white hover:bg-[#d94205]">{t("newQuestion")}</Badge>
              <CardTitle className="text-3xl">{t("questionList")}</CardTitle>
              <CardDescription className="mt-2 max-w-2xl text-white/70">{t("questionBankIntro")}</CardDescription>
            </div>
            <Button onClick={() => setBuilderOpen(true)} className="bg-white text-[#252525] hover:bg-[#fff4ec]">
              <Plus className="h-4 w-4" />
              {t("openQuestionBuilder")}
            </Button>
          </CardHeader>
        </Card>

        <Card>
          <CardHeader className="flex-row items-center justify-between">
            <div>
              <CardTitle>{t("questionList")}</CardTitle>
              <CardDescription>{t("questionListDesc")}</CardDescription>
            </div>
            <Button variant="outline" size="sm" onClick={reload}>{t("refresh")}</Button>
          </CardHeader>
          <CardContent>
            {loading ? <PageLoading /> : <QuestionTable questions={data?.questions || []} />}
          </CardContent>
        </Card>
      </div>

      <Sheet open={builderOpen} onOpenChange={setBuilderOpen}>
        <SheetContent className="w-full overflow-y-auto sm:max-w-3xl">
          <SheetHeader>
            <SheetTitle>{t("newQuestion")}</SheetTitle>
            <SheetDescription>{t("newQuestionDesc")}</SheetDescription>
          </SheetHeader>
          <form onSubmit={submit} className="mt-6 space-y-6">
            <Card className="border-[#ded6cc]">
              <CardHeader>
                <CardTitle className="text-lg">{t("questionContext")}</CardTitle>
                <CardDescription>{t("selectedStructure")}</CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <Field label={t("chooseTemplate")}>
                  <NativeSelect
                    value={form.templateId}
                    onChange={(value) => setForm((current) => ({ ...current, templateId: value, templateTaskId: "" }))}
                  >
                    {(templates.data?.templates || []).map((template) => (
                      <option key={template.id} value={template.id}>
                        {template.name} / {template.family.name} / {template.language.nativeName}
                      </option>
                    ))}
                  </NativeSelect>
                </Field>

                <Field label={t("choosePart")}>
                  <NativeSelect
                    value={form.templateTaskId}
                    onChange={(value) => setForm((current) => ({ ...current, templateTaskId: value }))}
                  >
                    {taskOptions.map((option) => (
                      <option key={option.taskId} value={option.taskId}>
                        {option.skillName} / {option.sectionName} / {option.taskName}
                      </option>
                    ))}
                  </NativeSelect>
                </Field>

                {templateLoading ? (
                  <PageLoading />
                ) : selectedTask ? (
                  <div className="grid gap-3 rounded-xl border bg-[#fffaf4] p-4 text-sm sm:grid-cols-3">
                    <InfoPill label={t("catalog")} value={`${selectedTask.familyName} / ${selectedTask.levelCode}`} />
                    <InfoPill label={t("skill")} value={selectedTask.skillName} />
                    <InfoPill label={t("type")} value={taskTypeLabel(selectedTask.taskType, locale)} />
                  </div>
                ) : (
                  <div className="rounded-xl border border-dashed bg-[#fffaf4] p-4 text-sm text-muted-foreground">
                    {form.templateId ? t("noTemplateTasks") : t("templateNeeded")}
                  </div>
                )}
              </CardContent>
            </Card>

            <Card className="border-[#ded6cc]">
              <CardHeader>
                <CardTitle className="text-lg">{t("questionStem")}</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <Field label={t("title")}>
                  <Input required value={form.title} onChange={(event) => setForm({ ...form, title: event.target.value })} />
                </Field>
                <Field label={t("questionStem")}>
                  <Textarea required rows={7} value={form.stem} onChange={(event) => setForm({ ...form, stem: event.target.value })} />
                </Field>
                <Field label={t("explanation")}>
                  <Textarea rows={3} value={form.explanation} onChange={(event) => setForm({ ...form, explanation: event.target.value })} />
                </Field>
                <div className="grid gap-3 sm:grid-cols-2">
                  <Field label={t("difficulty")}>
                    <Input
                      type="number"
                      min={1}
                      max={100}
                      value={form.difficulty}
                      onChange={(event) => setForm({ ...form, difficulty: Number(event.target.value) })}
                    />
                  </Field>
                  <Field label={t("status")}>
                    <NativeSelect value={form.status} onChange={(value) => setForm({ ...form, status: value })}>
                      {["draft", "review", "approved", "archived"].map((status) => (
                        <option key={status} value={status}>{statusLabel(status, locale)}</option>
                      ))}
                    </NativeSelect>
                  </Field>
                </div>
              </CardContent>
            </Card>

            <Card className="border-[#ded6cc]">
              <CardHeader>
                <CardTitle className="text-lg">{t("answerOptions")}</CardTitle>
                {manualTask && <CardDescription>{t("manualQuestionHint")}</CardDescription>}
              </CardHeader>
              {!manualTask && (
                <CardContent className="space-y-3">
                  {form.choices.map((choice, index) => (
                    <div key={index} className="grid grid-cols-[1fr_auto_auto] gap-2">
                      <Input
                        placeholder={`${t("option")} ${index + 1}`}
                        value={choice.text}
                        onChange={(event) => {
                          const choices = [...form.choices];
                          choices[index] = { ...choice, text: event.target.value };
                          setForm({ ...form, choices });
                        }}
                      />
                      <Button
                        type="button"
                        variant={choice.isCorrect ? "default" : "outline"}
                        onClick={() =>
                          setForm({
                            ...form,
                            choices: form.choices.map((item, itemIndex) => ({ ...item, isCorrect: itemIndex === index })),
                          })
                        }
                      >
                        {t("correct")}
                      </Button>
                      <Button
                        type="button"
                        variant="ghost"
                        disabled={form.choices.length <= 2}
                        onClick={() => setForm({ ...form, choices: form.choices.filter((_, itemIndex) => itemIndex !== index) })}
                      >
                        {t("remove")}
                      </Button>
                    </div>
                  ))}
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => setForm({ ...form, choices: [...form.choices, { text: "", isCorrect: false }] })}
                  >
                    <Plus className="h-4 w-4" />
                    {t("addOption")}
                  </Button>
                </CardContent>
              )}
            </Card>

            <div className="sticky bottom-0 -mx-6 border-t bg-white/95 px-6 py-4 backdrop-blur">
              <Button className="w-full" disabled={saving || !selectedTask}>
                {saving ? t("loading") : t("addQuestion")}
              </Button>
            </div>
          </form>
        </SheetContent>
      </Sheet>
    </Page>
  );
}

function createEmptyQuestionForm(): QuestionFormState {
  return {
    templateId: "",
    templateTaskId: "",
    title: "",
    stem: "",
    explanation: "",
    difficulty: 50,
    status: "approved",
    choices: [
      { text: "", isCorrect: true },
      { text: "", isCorrect: false },
      { text: "", isCorrect: false },
    ],
  };
}

function buildTemplateTaskOptions(template: TemplateDetail | null): TemplateTaskOption[] {
  if (!template) return [];
  return template.skills.flatMap((skill) =>
    skill.sections.flatMap((section) =>
      section.tasks.map((task) => ({
        templateId: template.id,
        templateName: template.name,
        familyId: template.family.id,
        familyName: template.family.name,
        languageId: template.language.id,
        languageName: template.language.nativeName,
        levelCode: template.levelCode,
        skillKey: skill.key,
        skillName: skill.name,
        sectionName: section.name,
        taskId: task.id,
        taskName: task.name,
        taskType: task.taskType,
        itemCount: task.itemCount,
        maxPoints: task.maxPoints,
      }))
    )
  );
}

function isManualTaskType(taskType: string) {
  return ["writing", "speaking", "upload"].includes(taskType);
}

function InfoPill({ label, value }: { label: string; value: string }) {
  return (
    <div className="rounded-lg border bg-white p-3">
      <div className="text-xs text-muted-foreground">{label}</div>
      <div className="mt-1 font-semibold">{value}</div>
    </div>
  );
}

function MockExamsPage() {
  const { t, locale } = useUi();
  const { data, loading, reload } = useData<{ mockExams: MockExam[] }>("/api/admin/mock-exams");
  const templates = useData<{ templates: TemplateSummary[] }>("/api/admin/templates");
  const [form, setForm] = useState({ title: "", templateId: "", visibility: "subscriber", status: "draft" });

  useEffect(() => {
    setForm((current) => ({ ...current, templateId: current.templateId || templates.data?.templates?.[0]?.id || "" }));
  }, [templates.data]);

  const submit = async (event: React.FormEvent) => {
    event.preventDefault();
    try {
      await postJson("/api/admin/mock-exams", form);
      toast.success(t("mockCreated"));
      setForm((current) => ({ ...current, title: "" }));
      reload();
    } catch (error) {
      toast.error(error instanceof Error ? error.message : t("mockCreateFailed"));
    }
  };

  return (
    <Page title={t("mockTitle")} description={t("mockDesc")}>
      <div className="grid gap-6 xl:grid-cols-[420px_1fr]">
        <Card>
          <CardHeader>
            <CardTitle>{t("newMock")}</CardTitle>
            <CardDescription>{t("newMockDesc")}</CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={submit} className="space-y-4">
              <Field label={t("mockName")}>
                <Input required value={form.title} onChange={(event) => setForm({ ...form, title: event.target.value })} />
              </Field>
              <Field label={t("templateLabel")}>
                <NativeSelect value={form.templateId} onChange={(value) => setForm({ ...form, templateId: value })}>
                  {(templates.data?.templates || []).map((template) => (
                    <option key={template.id} value={template.id}>{template.name}</option>
                  ))}
                </NativeSelect>
              </Field>
              <div className="grid grid-cols-2 gap-3">
                <Field label={t("visibility")}>
                  <NativeSelect value={form.visibility} onChange={(value) => setForm({ ...form, visibility: value })}>
                    <option value="private">{statusLabel("private", locale)}</option>
                    <option value="subscriber">{statusLabel("subscriber", locale)}</option>
                    <option value="public">{statusLabel("public", locale)}</option>
                  </NativeSelect>
                </Field>
                <Field label={t("status")}>
                  <NativeSelect value={form.status} onChange={(value) => setForm({ ...form, status: value })}>
                    <option value="draft">{statusLabel("draft", locale)}</option>
                    <option value="scheduled">{statusLabel("scheduled", locale)}</option>
                    <option value="published">{statusLabel("published", locale)}</option>
                  </NativeSelect>
                </Field>
              </div>
              <Button className="w-full">{t("createMock")}</Button>
            </form>
          </CardContent>
        </Card>
        <Card>
          <CardHeader>
            <CardTitle>{t("mocks")}</CardTitle>
            <CardDescription>{t("mocksDesc")}</CardDescription>
          </CardHeader>
          <CardContent>
            {loading ? <PageLoading /> : <MockTable mocks={data?.mockExams || []} />}
          </CardContent>
        </Card>
      </div>
    </Page>
  );
}


function SettingsPage() {
  const { t } = useUi();
  const languages = useData<{ languages: Language[] }>("/api/admin/languages");
  const catalog = useData<{ families: Family[]; levels: Level[] }>("/api/admin/exam-families");
  const [language, setLanguage] = useState({ code: "", name: "", nativeName: "" });
  const [family, setFamily] = useState({ code: "", name: "", description: "", defaultLanguageCode: "en" });

  const addLanguage = async (event: React.FormEvent) => {
    event.preventDefault();
    await postJson("/api/admin/languages", language);
    toast.success(t("languageAdded"));
    setLanguage({ code: "", name: "", nativeName: "" });
    languages.reload();
  };

  const addFamily = async (event: React.FormEvent) => {
    event.preventDefault();
    await postJson("/api/admin/exam-families", family);
    toast.success(t("familyAdded"));
    setFamily({ code: "", name: "", description: "", defaultLanguageCode: "en" });
    catalog.reload();
  };

  const seed = async () => {
    await postJson("/api/admin/seed", {});
    toast.success(t("catalogChecked"));
    languages.reload();
    catalog.reload();
  };

  return (
    <Page title={t("settingsTitle")} description={t("settingsDesc")}>
      <div className="mb-6 flex justify-end">
        <Button variant="outline" onClick={seed}>
          <CheckCircle2 className="h-4 w-4" />
          {t("verifyCatalog")}
        </Button>
      </div>
      <div className="grid gap-6 lg:grid-cols-2">
        <Card>
          <CardHeader>
            <CardTitle>{t("languagesTitle")}</CardTitle>
            <CardDescription>{t("languagesDesc")}</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <form onSubmit={addLanguage} className="grid gap-3 sm:grid-cols-[80px_1fr_1fr_auto]">
              <Input placeholder="de" value={language.code} onChange={(event) => setLanguage({ ...language, code: event.target.value })} />
              <Input placeholder="German" value={language.name} onChange={(event) => setLanguage({ ...language, name: event.target.value })} />
              <Input placeholder="Deutsch" value={language.nativeName} onChange={(event) => setLanguage({ ...language, nativeName: event.target.value })} />
              <Button><Plus className="h-4 w-4" /></Button>
            </form>
            <SimpleList
              items={(languages.data?.languages || []).map((item) => ({
                title: `${item.nativeName} (${item.code})`,
                description: item.name,
              }))}
            />
          </CardContent>
        </Card>
        <Card>
          <CardHeader>
            <CardTitle>{t("examFamiliesTitle")}</CardTitle>
            <CardDescription>{t("examFamiliesDesc")}</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <form onSubmit={addFamily} className="space-y-3">
              <div className="grid gap-3 sm:grid-cols-[100px_1fr]">
                <Input placeholder="ielts" value={family.code} onChange={(event) => setFamily({ ...family, code: event.target.value })} />
                <Input placeholder="IELTS" value={family.name} onChange={(event) => setFamily({ ...family, name: event.target.value })} />
              </div>
              <Input placeholder={t("description")} value={family.description} onChange={(event) => setFamily({ ...family, description: event.target.value })} />
              <Button>{t("addExamFamily")}</Button>
            </form>
            <SimpleList
              items={(catalog.data?.families || []).map((item) => ({
                title: `${item.name} (${item.code})`,
                description: item.description || "",
              }))}
            />
          </CardContent>
        </Card>
      </div>
    </Page>
  );
}

function TemplateDetailCard({ template, onStatus }: { template: TemplateDetail; onStatus: (id: string, status: string) => void }) {
  const { t, locale } = useUi();
  return (
    <Card>
      <CardHeader className="flex-row items-start justify-between">
        <div>
          <CardTitle>{template.name}</CardTitle>
          <CardDescription>
            {template.family.name} / {template.language.nativeName} / {template.levelCode}
          </CardDescription>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" size="sm" onClick={() => onStatus(template.id, "review")}>{t("review")}</Button>
          <Button size="sm" onClick={() => onStatus(template.id, "published")}>{t("publish")}</Button>
        </div>
      </CardHeader>
      <CardContent className="space-y-4">
        {template.skills.map((skill) => (
          <div key={skill.id} className="rounded-lg border bg-[#fffaf4] p-4">
            <div className="mb-3 flex items-center justify-between">
              <div className="font-semibold">{skill.name}</div>
              <Badge variant="outline">{formatDuration(skill.durationSec, t("minutes"))} / {skill.maxPoints} {t("pointCount")}</Badge>
            </div>
            <div className="space-y-3">
              {skill.sections.map((section) => (
                <div key={section.id} className="rounded-lg border bg-white p-3">
                  <div className="mb-2 flex items-center justify-between">
                    <div>
                      <div className="text-sm font-medium">{section.name}</div>
                      <div className="text-xs text-muted-foreground">{section.instructions}</div>
                    </div>
                    <Badge variant="secondary">{section.tasks.length} {t("partLabel")}</Badge>
                  </div>
                  <div className="grid gap-2 md:grid-cols-2">
                    {section.tasks.map((task) => (
                      <div key={task.id} className="flex items-center justify-between rounded-md bg-[#f7f2ec] px-3 py-2 text-sm">
                        <span>{task.name}</span>
                        <span className="text-muted-foreground">{taskTypeLabel(task.taskType, locale)} / {task.itemCount}</span>
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </div>
        ))}
      </CardContent>
    </Card>
  );
}

function TemplateTable({
  templates,
  onSelect,
  compact,
}: {
  templates: TemplateSummary[];
  onSelect?: (id: string) => void;
  compact?: boolean;
}) {
  const { t } = useUi();
  if (!templates.length) return <EmptyState title={t("noTemplatesTitle")} description={t("noTemplatesDesc")} />;
  return (
    <div className="overflow-hidden rounded-lg border">
      <table className="w-full text-left text-sm">
        <thead className="bg-[#f7f2ec] text-xs uppercase text-[#666]">
          <tr>
            <th className="px-3 py-2">{t("tableTemplate")}</th>
            <th className="px-3 py-2">{t("tableLanguage")}</th>
            <th className="px-3 py-2">{t("tableStructure")}</th>
            <th className="px-3 py-2">{t("tableStatus")}</th>
            {!compact && <th className="px-3 py-2" />}
          </tr>
        </thead>
        <tbody className="divide-y bg-white">
          {templates.map((template) => (
            <tr key={template.id} className="hover:bg-[#fffaf4]">
              <td className="px-3 py-3">
                <div className="font-medium">{template.name}</div>
                <div className="text-xs text-muted-foreground">{template.family.name} / {template.levelCode}</div>
              </td>
              <td className="px-3 py-3">{template.language.nativeName}</td>
              <td className="px-3 py-3">{template.skillCount} {t("sectionLabel")} / {template.taskCount} {t("partLabel")}</td>
              <td className="px-3 py-3"><StatusBadge status={template.status} /></td>
              {!compact && (
                <td className="px-3 py-3 text-right">
                  <Button variant="ghost" size="sm" onClick={() => onSelect?.(template.id)}>
                    {t("open")}
                    <ChevronRight className="h-4 w-4" />
                  </Button>
                </td>
              )}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function QuestionTable({ questions }: { questions: Question[] }) {
  const { t, locale } = useUi();
  if (!questions.length) return <EmptyState title={t("noQuestionsTitle")} description={t("noQuestionsDesc")} />;
  return (
    <div className="grid gap-3">
      {questions.map((question) => (
        <div key={question.id} className="rounded-2xl border bg-white p-4 transition-colors hover:bg-[#fffaf4]">
          <div className="flex flex-col justify-between gap-4 xl:flex-row xl:items-center">
            <div className="min-w-0 flex-1">
              <div className="mb-2 flex flex-wrap items-center gap-2">
                <Badge variant="outline" className="border-[#d94205] text-[#d94205]">
                  {question.family?.name || t("family")} / {question.levelCode}
                </Badge>
                <Badge variant="outline">{question.language.nativeName || question.language.code.toUpperCase()}</Badge>
                <Badge variant="outline">{skillLabel(question.skillKey, locale)}</Badge>
              </div>
              <div className="text-base font-semibold">{question.title}</div>
              <div className="mt-1 line-clamp-2 text-sm text-muted-foreground">{question.stem}</div>
            </div>
            <div className="grid min-w-[260px] gap-2 sm:grid-cols-2">
              <InfoPill label={t("type")} value={taskTypeLabel(question.taskType, locale)} />
              <InfoPill label={t("option")} value={String(question.choiceCount || 0)} />
            </div>
            <StatusBadge status={question.status} />
          </div>
        </div>
      ))}
    </div>
  );
}

function MockTable({ mocks }: { mocks: MockExam[] }) {
  const { t, locale } = useUi();
  if (!mocks.length) return <EmptyState title={t("noMocksTitle")} description={t("noMocksDesc")} />;
  return (
    <div className="grid gap-3">
      {mocks.map((mock) => (
        <div key={mock.id} className="flex flex-col gap-3 rounded-lg border bg-white p-4 sm:flex-row sm:items-center sm:justify-between">
          <div>
            <div className="font-medium">{mock.title}</div>
            <div className="text-sm text-muted-foreground">{mock.template.name} / {statusLabel(mock.visibility, locale)}</div>
            <div className="mt-2 flex flex-wrap gap-2 text-xs text-muted-foreground">
              <Badge variant="outline">{mock.taskCount || 0} {t("taskCount")}</Badge>
              <Badge variant="outline">{mock.questionCount || 0} {t("questionCount")}</Badge>
              <Badge variant="outline">{mock.pointTotal || 0} {t("pointCount")}</Badge>
            </div>
          </div>
          <StatusBadge status={mock.status} />
        </div>
      ))}
    </div>
  );
}


function SimpleList({ items }: { items: Array<{ title: string; description: string }> }) {
  return (
    <div className="divide-y rounded-lg border bg-white">
      {items.map((item) => (
        <div key={item.title} className="p-3">
          <div className="text-sm font-medium">{item.title}</div>
          {item.description && <div className="text-xs text-muted-foreground">{item.description}</div>}
        </div>
      ))}
    </div>
  );
}

function Page({ title, description, children }: { title: string; description: string; children: React.ReactNode }) {
  return (
    <section>
      <div className="mb-6 flex flex-col justify-between gap-4 md:flex-row md:items-end">
        <div>
          <h1 className="text-3xl font-bold text-[#171717]">{title}</h1>
          <p className="mt-1 max-w-3xl text-sm text-muted-foreground">{description}</p>
        </div>
      </div>
      {children}
    </section>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="space-y-2">
      <Label>{label}</Label>
      {children}
    </div>
  );
}

function NativeSelect({
  value,
  onChange,
  children,
}: {
  value: string;
  onChange: (value: string) => void;
  children: React.ReactNode;
}) {
  return (
    <select
      value={value}
      onChange={(event) => onChange(event.target.value)}
      className="h-9 w-full rounded-md border border-input bg-background px-3 text-sm shadow-sm focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
    >
      {children}
    </select>
  );
}

function StatusBadge({ status }: { status: string }) {
  const { locale } = useUi();
  const tone =
    status === "published" || status === "approved" || status === "active"
      ? "bg-emerald-50 text-emerald-700 border-emerald-200"
      : status === "review" || status === "scheduled"
        ? "bg-amber-50 text-amber-800 border-amber-200"
        : "bg-[#f7f2ec] text-[#5b5148] border-[#e0d4c7]";

  return <Badge variant="outline" className={tone}>{statusLabel(status, locale)}</Badge>;
}

function statusLabel(status: string, locale: UiLocale) {
  const labels: Record<UiLocale, Record<string, string>> = {
    en: {
      draft: "Draft",
      review: "Review",
      published: "Published",
      archived: "Archived",
      scheduled: "Scheduled",
      approved: "Approved",
      active: "Active",
      private: "Private",
      public: "Public",
      subscriber: "Subscribers",
    },
    tr: {
      draft: "Taslak",
      review: "Kontrol",
      published: "Yayinda",
      archived: "Arsiv",
      scheduled: "Planli",
      approved: "Onayli",
      active: "Aktif",
      private: "Gizli",
      public: "Herkese acik",
      subscriber: "Aboneler",
    },
    de: {
      draft: "Entwurf",
      review: "Pruefung",
      published: "Veroeffentlicht",
      archived: "Archiv",
      scheduled: "Geplant",
      approved: "Freigegeben",
      active: "Aktiv",
      private: "Privat",
      public: "Oeffentlich",
      subscriber: "Abonnenten",
    },
  };

  return labels[locale][status] || status;
}

function taskTypeLabel(type: string, locale: UiLocale) {
  const labels: Record<UiLocale, Record<string, string>> = {
    en: {
      mcq: "Multiple choice",
      multi_select: "Multiple select",
      match: "Matching",
      order: "Ordering",
      gap: "Fill gaps",
      short: "Short answer",
      writing: "Writing",
      speaking: "Speaking",
      upload: "File upload",
    },
    tr: {
      mcq: "Coktan secmeli",
      multi_select: "Coklu secim",
      match: "Eslestirme",
      order: "Siralamali",
      gap: "Bosluk doldurma",
      short: "Kisa cevap",
      writing: "Yazma",
      speaking: "Konusma",
      upload: "Dosya yukleme",
    },
    de: {
      mcq: "Multiple Choice",
      multi_select: "Mehrfachauswahl",
      match: "Zuordnung",
      order: "Reihenfolge",
      gap: "Luecken",
      short: "Kurzantwort",
      writing: "Schreiben",
      speaking: "Sprechen",
      upload: "Datei-Upload",
    },
  };

  return labels[locale][type] || type;
}

function skillLabel(skill: string, locale: UiLocale) {
  const labels: Record<UiLocale, Record<string, string>> = {
    en: {
      lesen: "Reading",
      reading: "Reading",
      hoeren: "Listening",
      listening: "Listening",
      sprachbausteine: "Language use",
      language_use: "Language use",
      schreiben: "Writing",
      writing: "Writing",
      sprechen: "Speaking",
      speaking: "Speaking",
    },
    tr: {
      lesen: "Okuma",
      reading: "Okuma",
      hoeren: "Dinleme",
      listening: "Dinleme",
      sprachbausteine: "Dil bilgisi",
      language_use: "Dil bilgisi",
      schreiben: "Yazma",
      writing: "Yazma",
      sprechen: "Konusma",
      speaking: "Konusma",
    },
    de: {
      lesen: "Lesen",
      reading: "Lesen",
      hoeren: "Hoeren",
      listening: "Hoeren",
      sprachbausteine: "Sprachbausteine",
      language_use: "Sprachbausteine",
      schreiben: "Schreiben",
      writing: "Schreiben",
      sprechen: "Sprechen",
      speaking: "Sprechen",
    },
  };

  return labels[locale][skill] || skill;
}

function EmptyState({ title, description }: { title: string; description: string }) {
  return (
    <div className="grid min-h-40 place-items-center rounded-lg border border-dashed bg-[#fffaf4] p-8 text-center">
      <div>
        <Archive className="mx-auto mb-3 h-8 w-8 text-[#d94205]" />
        <div className="font-medium">{title}</div>
        <div className="mt-1 text-sm text-muted-foreground">{description}</div>
      </div>
    </div>
  );
}

function PageLoading() {
  const { t } = useUi();
  return <div className="rounded-lg border bg-white p-4 text-sm text-muted-foreground">{t("dataLoading")}</div>;
}

function useData<T>(path: string) {
  const { t } = useUi();
  const [data, setData] = useState<T | null>(null);
  const [loading, setLoading] = useState(true);

  const reload = async () => {
    setLoading(true);
    try {
      setData(await apiFetch<T>(path));
    } catch (error) {
      toast.error(error instanceof Error ? error.message : t("dataError"));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    reload();
  }, [path]);

  return { data, loading, reload };
}

function formatDuration(seconds: number, unit = "min") {
  if (!seconds) return `0 ${unit}`;
  return `${Math.round(seconds / 60)} ${unit}`;
}

function AppRoutes() {
  return (
    <Routes>
      <Route path="/setup" element={<SetupPage />} />
      <Route path="/login" element={<LoginPage />} />
      <Route
        path="/student/*"
        element={
          <RequireAuth>
            <StudentPortal />
          </RequireAuth>
        }
      />
      <Route
        path="/admin"
        element={
          <RequireAuth>
            <AdminShell />
          </RequireAuth>
        }
      >
        <Route index element={<DashboardPage />} />
        <Route path="templates" element={<TemplateStudioPage />} />
        <Route path="questions" element={<QuestionBankPage />} />
        <Route path="mock-exams" element={<MockExamsPage />} />
        <Route path="media" element={<Navigate to="/admin/templates" replace />} />
        <Route path="settings" element={<SettingsPage />} />
      </Route>
      <Route path="*" element={<Navigate to="/admin" replace />} />
    </Routes>
  );
}

export default function App() {
  return (
    <BrowserRouter>
      <UiProvider>
        <AuthProvider>
          <AppRoutes />
          <Toaster richColors position="top-right" />
        </AuthProvider>
      </UiProvider>
    </BrowserRouter>
  );
}
